/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import {
  CloseCircleFilled,
  QuestionCircleFilled,
  CheckCircleFilled,
  MedicineBoxFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { ATTENDANCE_SUPPORT_URL } from '@sportninja/common/features/game/attendance/constants'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import QuestionIcon from 'src/components/icons/QuestionIcon'
import { weightMedium } from 'src/components/css'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import BaseModal from '../../ts-components/BaseModal'
import Text from '../../ts-components/Text'
import { ATTENDANCE_STATUS_COPY } from '../utils/statusCopy'

export interface AttendanceHelpModalProps {
  open: boolean
  onClose: () => void
}

interface StatusItem {
  key: string
  Icon: React.ComponentType<{ style?: React.CSSProperties }>
  color: string
}

// Copy comes from the shared ATTENDANCE_STATUS_COPY map so the row tooltips and
// this modal never drift.
const STATUSES: StatusItem[] = [
  { key: 'not_attending', Icon: CloseCircleFilled, color: colors.FIERY_RED },
  {
    key: 'maybe_attending',
    Icon: QuestionCircleFilled,
    color: colors.AMERICAN_YELLOW,
  },
  { key: 'attending', Icon: CheckCircleFilled, color: colors.GREEN_GOBLIN },
  { key: 'injury', Icon: MedicineBoxFilled, color: colors.FIERY_RED },
  {
    key: 'suspended',
    Icon: ExclamationCircleFilled,
    color: colors.FIERY_RED,
  },
]

const introStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`

const subtitleStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 14px;
  line-height: 20px;
  margin: 0;
`

const listStyle = css`
  display: flex;
  flex-direction: column;
`

const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid ${colors.SECONDARY_200};
`

const iconStyle = (color: string): React.CSSProperties => ({
  fontSize: 24,
  color,
  flexShrink: 0,
})

// Title + the "?" help link, same as the Family Management modal header.
const headerRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const infoButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 0;

  &:hover {
    opacity: 0.85;
  }
`

const linkStyle = css`
  ${weightMedium}
  color: ${colors.PRIMARY};
  text-decoration: none;

  &:hover {
    color: ${colors.PRIMARY};
    opacity: 0.7;
  }
`

const statusTitleStyle = css`
  color: ${colors.NEUTRAL_0};
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  margin: 0;
`

const statusDescStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 12px;
  line-height: 17px;
  margin: 2px 0 0;
`

const AttendanceHelpModal: React.FC<AttendanceHelpModalProps> = ({
  open,
  onClose,
}) => (
  <BaseModal
    isOpen={open}
    onClose={onClose}
    title={
      <div css={headerRowStyle}>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords('Availability Status')}
        </Text>
        <Tooltip
          title={capitalizeWords('How Game Attendance Works')}
          zIndex={MODAL_TOKENS.Z_INDEX_TOOLTIP}
        >
          <a
            css={infoButtonStyle}
            href={ATTENDANCE_SUPPORT_URL}
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Learn more about game attendance'
          >
            <QuestionIcon size={22} color={colors.PRIMARY} />
          </a>
        </Tooltip>
      </div>
    }
    desktopWidth='480px'
    actions={<span />}
    elevated
  >
    <div css={introStyle}>
      <p css={subtitleStyle}>
        These are the statuses set by the player availability feature. You can
        review this information at anytime by pressing the info icon.
      </p>
      <p css={subtitleStyle}>
        For more information about this feature,{' '}
        <a
          css={linkStyle}
          href={ATTENDANCE_SUPPORT_URL}
          target='_blank'
          rel='noopener noreferrer'
        >
          Click Here.
        </a>
      </p>
    </div>
    <div css={listStyle}>
      {STATUSES.map(({ key, Icon, color }) => (
        <div key={key} css={rowStyle}>
          <Icon style={iconStyle(color)} />
          <div>
            <p css={statusTitleStyle}>{ATTENDANCE_STATUS_COPY[key].title}</p>
            <p css={statusDescStyle}>
              {ATTENDANCE_STATUS_COPY[key].description}
            </p>
          </div>
        </div>
      ))}
    </div>
  </BaseModal>
)

export default AttendanceHelpModal
